import {
	IconButton,
	Card,
	CardContent,
	Chip,
	Tooltip,
	Typography,
	Grid,
} from '@mui/material';

import { useCallback } from 'react';

import { useDispatch } from 'react-redux';

import { ReactComponent as AIGenerate } from 'common/assets/images/ai-generate.svg';
import { ReactComponent as Reviewed } from 'common/assets/images/hand.svg';
import { ReactComponent as High } from 'common/assets/images/high-ai-score.svg';
import { ReactComponent as Low } from 'common/assets/images/low-ai-score.svg';
import { ReactComponent as Medium } from 'common/assets/images/medium-ai-score.svg';
import {
	ENTITY_AI_SCORE,
	ENTITY_STATUS,
	ENTITY_TYPE,
} from 'common/constants/TaxonomyConstants';
import {
	setMappingDrawer,
	setSelectedEntityTypeData,
} from 'common/store/reducer/commonReducer';
import { setTabIndex } from 'common/store/reducer/mappingDetailsReducer';
import { pascalFormat } from 'common/utils/utilFunctions';

import { getRelationshipCardBorderColor } from './mappingUtils';

import styles from './RelationMapping.module.scss';

interface IAIMappingCardProps {
	cardData: any;
	sideDrawerHandler: any;
	isFromHorizonScan?: boolean;
	enabled?: boolean;
	citationGroupBusinessUnitIds?: string[];
}

const AIMappingCard = (props: IAIMappingCardProps) => {
	const dispatch = useDispatch();
	const {
		cardData,
		sideDrawerHandler,
		isFromHorizonScan = false,
		enabled,
		citationGroupBusinessUnitIds,
	} = props;

	const {
		childType,
		type = childType,
		childName,
		generateMode,
		reviewStatus = 'Add',
		aiScore = 'High',
		id,
		childId,
		parentId,
		createdBy,
		childDisplayId,
	} = cardData;

	const status = reviewStatus || ENTITY_STATUS.ADD;

	const clickHandler = useCallback(async () => {
		if (isFromHorizonScan && !enabled) return;
		dispatch(setMappingDrawer(true));
		dispatch(setTabIndex('overview'));
		dispatch(
			setSelectedEntityTypeData({
				...cardData,
				type,
				aiScore,
				reviewStatus,
				generateMode,
				id: isFromHorizonScan ? id : childId,
				uniqueId: id,
				parentId,
				createdBy,
				title: childName,
				childDisplayId,
				citationGroupBusinessUnitIds,
			}),
		);
		sideDrawerHandler();
	}, [cardData, setMappingDrawer, setSelectedEntityTypeData]);

	const getChipColor = () => {
		switch (status.toLowerCase()) {
			case ENTITY_STATUS.ADD.toLowerCase():
				return styles.addchip;
			case ENTITY_STATUS.REVIEWED.toLowerCase():
				return styles.reviewed;
			case ENTITY_STATUS.IMPACTED.toLowerCase():
				return styles.impacted;
			case ENTITY_STATUS.PENDING_REVIEW.toLowerCase():
				return styles.notimpacted;
			case ENTITY_STATUS.NOT_IMPACTED.toLowerCase():
				return styles.pendingreview;
			default:
				return '';
		}
	};
	const cardTypeIcon = () => {
		if (generateMode === 'AI') {
			return <AIGenerate className={styles['dots-icon']} />;
		}
		return <Reviewed className={styles['dots-icon']} />;
	};

	const getAIScoreIcon = () => {
		switch (aiScore?.toLowerCase()) {
			case ENTITY_AI_SCORE.HIGH:
				return <High className={styles['dots-icon']} />;
			case ENTITY_AI_SCORE.MEDIUM:
				return <Medium className={styles['dots-icon']} />;
			case ENTITY_AI_SCORE.LOW:
				return <Low className={styles['dots-icon']} />;
			default:
				return null;
		}
	};

	return (
		<Card
			onClick={() => clickHandler()}
			className={`${
				styles['mapping-ai-card-container']
			} ${getRelationshipCardBorderColor(type)} ${
				isFromHorizonScan
					? styles['mapping-ai-card-horizon']
					: styles['mapping-ai-card-reg-inventory']
			} ${enabled ? styles['cursor-pointer'] : styles['cursor-not-allowed']}`}
			data-testid="mapping-card"
			key={id}
		>
			<CardContent className={styles['map-content-card']}>
				<Grid container display="flex" justifyContent="space-between">
					<Grid item>
						<Typography className={styles['mapping-ai-card-title']}>
							{`${
								type === ENTITY_TYPE.GROUP
									? ENTITY_TYPE.OBLIGATION
									: pascalFormat(type)
							}`}
						</Typography>
					</Grid>

					<Grid item>
						<div className={styles['mapping-ai-img-container']}>
							<IconButton
								aria-label="delete"
								data-testid={`Relationship-dots-icon-${childName}`}
							>
								{cardTypeIcon()}
							</IconButton>
						</div>
					</Grid>
				</Grid>
				<Grid item>
					<Typography className={styles['mapping-ai-card-title']}>
						{!isFromHorizonScan ? childDisplayId : ''}
					</Typography>
					<Typography className={styles['mapping-ai-card-sub-title']}>
						<Tooltip title={childName} placement="bottom">
							<div className={styles['card-title-tooltip']}>{childName}</div>
						</Tooltip>
						{cardData?.paragraphId && (
							<Typography className={styles['paragraph-id-style']}>
								{cardData?.paragraphId}
							</Typography>
						)}
					</Typography>
				</Grid>
				<Grid item>
					<Typography className={styles['mapping-ai-card-title']}>
						{cardData?.businessUnitName || ''}
					</Typography>
				</Grid>
				<Grid
					container
					display="flex"
					justifyContent="space-between"
					className={`${styles['mapping-ai-chip-grid']} ${
						isFromHorizonScan && styles['mapping-ai-chip-horizon']
					}`}
				>
					<Grid item sm={generateMode === 'AI' ? 6 : 12}>
						<Tooltip title={status}>
							<Chip
								key={type}
								label={status}
								className={`${styles['mapping-ai-chip']} ${getChipColor()}`}
								variant="outlined"
								sx={{ mr: 0.5, mb: 0.5 }}
							/>
						</Tooltip>
					</Grid>
					{generateMode === 'AI' && (
						<Grid item sm={6}>
							<div className={styles['mapping-ai-img-container']}>
								<IconButton
									aria-label="delete"
									data-testid={`Relationship-dots-icon-${childName}`}
								>
									{getAIScoreIcon()}
								</IconButton>
								<Tooltip
									title={
										aiScore
											? `${aiScore[0].toUpperCase()}${aiScore.slice(
													1,
											  )} AI Score`
											: ''
									}
									placement="bottom"
								>
									<div
										className={
											aiScore && aiScore.toLowerCase() === ENTITY_AI_SCORE.HIGH
												? styles['mapping-ai-card-score-high']
												: styles['mapping-ai-card-score-medium']
										}
									>
										{aiScore
											? `${aiScore[0].toUpperCase()}${aiScore.slice(1)}`
											: ''}{' '}
										AI Score
									</div>
								</Tooltip>
							</div>
						</Grid>
					)}
				</Grid>
			</CardContent>
		</Card>
	);
};

export default AIMappingCard;
